import { Pie, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from "chart.js";
import { useState } from "react";
import { getColorForKey } from "./utils/colors";
import apiFetch from "./utils/api";

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
);

const ResourceChart = ({ selectedResource, subcodeBreakdown, projectSubcodeMatrix, year, month, onClose, loading }) => {

    const [drillRows, setDrillRows] = useState([]);
    const [drillOpen, setDrillOpen] = useState(false);
    const [drillTitle, setDrillTitle] = useState("");

    return (
        <div style={{minHeight: 320}}>
            <div style={{ marginTop: 20 }}>
                <button onClick={() => (onClose ? onClose() : null)}>Back</button>
            </div>
            <div
                style={{
                    display: "flex",
                    gap: 20,
                    flexWrap: "wrap",
                    justifyContent: "center"
                }}
            >
                <div
                    style={{
                        flex: "1 1 320px",
                        maxWidth: 600,
                        minWidth: 320,
                        height: "auto",
                    }}
                >
                    <h3 style={{ textAlign: "center" }}>
                        Activity breakdown for {selectedResource.code}
                    </h3>
                    <div style={{ height: 240 + subcodeBreakdown.length * 5 }}>
                        <Pie
                            data={{
                                labels: subcodeBreakdown.map((s) => s.sub || "(none)"),
                                datasets: [
                                    {
                                        data: subcodeBreakdown.map((s) =>
                                            Number(s.hours || 0)
                                        ),
                                        backgroundColor: subcodeBreakdown.map((s) =>
                                            getColorForKey(s.sub)
                                        ),
                                    },
                                ],
                            }}
                            options={{ responsive: true, maintainAspectRatio: false }}
                            height={240 + subcodeBreakdown.length * 5}
                        />
                    </div>
                    {/* Activity / subcode buttons below the pie chart */}
                    <div
                        style={{
                            marginTop: 8,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        {subcodeBreakdown.map((s, i) => {
                            const key = s.id + "_" + i;
                            return (
                                <button
                                    key={key}
                                    onClick={async () => {
                                        try {
                                            loading(true)
                                            const sub = s.sub || "";
                                            const r = encodeURIComponent(selectedResource.id);
                                            const qs = `user=${r}&activity=${encodeURIComponent(s.id)}&start=${year}-${ month !== null ? String(month).padStart(2, "0") : "01"}-01&end=${year}-${ month !== null ? String(month).padStart(2, "0") : "12"}-31`;
                                            setDrillTitle(
                                                `${selectedResource.code || selectedResource
                                                } — ${sub}`
                                            );
                                            setDrillOpen(true);
                                            const rows = await apiFetch(`/api/timesheets?${qs}`);
                                            setDrillRows(Array.isArray(rows) ? rows : []);
                                            loading(false)
                                        } catch (e) { }
                                    }}
                                >
                                    {s.sub}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {projectSubcodeMatrix && projectSubcodeMatrix.projects && (
                    <div
                        style={{
                            flex: "1 1 320px",
                            maxWidth: 600,
                            minWidth: 320,
                            height: "auto",
                        }}
                    >
                        <h3 style={{ textAlign: "center" }}>
                            Projects by Activity
                        </h3>
                        <div style={{ height: 240 + subcodeBreakdown.length * 5 }}>
                            <Bar
                                data={{
                                    labels: projectSubcodeMatrix.projects.map(p => p.code),
                                    datasets: projectSubcodeMatrix.subCodes.map(
                                        (sub) => ({
                                            label: sub,
                                            data:
                                                projectSubcodeMatrix.matrix[sub] ||
                                                projectSubcodeMatrix.projects.map(() => 0),
                                            backgroundColor: getColorForKey(sub),
                                        })
                                    ),
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        x: { stacked: true },
                                        y: { stacked: true, beginAtZero: true },
                                    },
                                }}
                                height={240 + subcodeBreakdown.length * 5}
                            />
                        </div>
                        {/* Project buttons below the bar chart */}
                        <div
                            style={{
                                marginTop: 8,
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            {projectSubcodeMatrix.projects.map((p, i) => (
                                <button
                                    key={p.id + "_" + i}
                                    onClick={async () => {
                                        try {
                                            loading(true)
                                            const r = encodeURIComponent(selectedResource.id);
                                            const pr = encodeURIComponent(p.id);
                                            const qs = `user=${r}&project=${pr}&start=${year}-${ month !== null ? String(month).padStart(2, "0") : "01"}-01&end=${year}-${ month !== null ? String(month).padStart(2, "0") : "12"}-31`;
                                            setDrillTitle(`${p.code}`);
                                            setDrillOpen(true);
                                            const rows = await apiFetch(`/api/timesheets?${qs}`);
                                            setDrillRows(Array.isArray(rows) ? rows : []);
                                            loading(false)
                                        } catch (e) { }
                                    }}
                                >
                                    {p.code}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {drillOpen && (
                <div
                    style={{
                        position: "fixed",
                        left: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                    onClick={() => setDrillOpen(false)}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: 16,
                            borderRadius: 6,
                            minWidth: 600,
                            maxHeight: "80%",
                            overflow: "auto",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 12,
                            }}
                        >
                            <h3 style={{ margin: 0 }}>{`Entries for ${drillTitle}`}</h3>
                            <button onClick={() => setDrillOpen(false)}>Close</button>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>Date</th>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>
                                        Resource
                                    </th>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>
                                        Project
                                    </th>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>Sub</th>
                                    <th
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 6,
                                            textAlign: "right",
                                        }}
                                    >
                                        Hours
                                    </th>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>
                                        Division
                                    </th>
                                    <th style={{ border: "1px solid #ddd", padding: 6 }}>
                                        Notes
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(drillRows || []).map((r, i) => (
                                    <tr key={r.id + "_" + i}>
                                        <td style={{ border: "1px solid #eee", padding: 6 }}>
                                            {r.TimesheetDate ? new Date(r.TimesheetDate).toLocaleDateString() : ""}
                                        </td>
                                        <td style={{ border: "1px solid #eee", padding: 6 }}>
                                            {r.UserCode}
                                        </td>
                                        <td style={{ border: "1px solid #eee", padding: 6 }}>
                                            {r.ProjectCode}
                                        </td>
                                        <td style={{ border: "1px solid #eee", padding: 6 }}>
                                            {r.ActivityCode}
                                        </td>
                                        <td
                                            style={{
                                                border: "1px solid #eee",
                                                padding: 6,
                                                textAlign: "right",
                                            }}
                                        >
                                            {Number(r.TimesheetHours || 0).toFixed(2)}
                                        </td>
                                        <td style={{ border: "1px solid #eee", padding: 6 }}>
                                            {r.TimesheetDivision}
                                        </td>
                                        <td
                                            style={{
                                                border: "1px solid #eee",
                                                padding: 6,
                                                whiteSpace: "pre-wrap",
                                            }}
                                        >
                                            {r.TimesheetNotes}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ResourceChart